import { User } from '@bioliberty/lifemind';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { useDispatch, useSelector } from 'react-redux';
import { useLazyGetUsersQuery } from '../../api';
import { useAuthentication } from '../../auth';
import { useTenantIds } from '../../hooks/useTenantIds';
import { getUsers as getAdminViewUsers, setUsers } from '../../stores/slices/admin-view';
import { hasAdminAccess } from '../../util/auth';

export function useSearchUsers({ searchTerm }: { searchTerm?: string }): {
  users: User[];
  isLoading: boolean;
} {
  const currentUser = useAuthentication();
  const dispatch = useDispatch();
  const tenantIds = useTenantIds();
  const users = useSelector(getAdminViewUsers());
  const [isLoading, setIsLoading] = useState(false);
  const [getUsers] = useLazyGetUsersQuery();
  const { showBoundary } = useErrorBoundary();

  const fetchAllUsers = useCallback(async () => {
    setIsLoading(true);

    let allUsers: User[] = [];
    let pageToken = undefined;

    try {
      do {
        const preferCachedValue = true;
        const { data } = await getUsers({ pageToken }, preferCachedValue);
        if (data) {
          allUsers = [...allUsers, ...data.items];
          pageToken = data.next;
        }
      } while (pageToken);

      dispatch(setUsers(allUsers));
    } catch (error) {
      showBoundary(error);
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, getUsers, showBoundary]);

  useEffect(() => {
    if (hasAdminAccess(currentUser)) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      fetchAllUsers();
    }
  }, [currentUser, fetchAllUsers]);

  const filteredUsers = useMemo(() => {
    const iSearchTerm = searchTerm?.toLowerCase();
    return users.filter((user) => {
      const fullName = `${user.firstName} ${user.lastName}`.toLowerCase();

      const matchesSearch = !iSearchTerm || fullName.includes(iSearchTerm);

      const matchesTenants =
        !user.subtenants?.length || user.subtenants.some((tenantId) => tenantIds?.includes(tenantId));

      return matchesSearch && matchesTenants;
    });
  }, [searchTerm, users, tenantIds]);

  return { users: filteredUsers, isLoading };
}
